import { AfterViewInit, Directive, ElementRef, HostBinding, Input, NgZone, OnDestroy, Renderer2 } from "@angular/core";
import { Bind } from "../../decorators/bind.decorator";
import { AriaLive } from "../../services/aria.live.service";

export const S25_DND_SORTABLE_IDLE_INSTRUCTIONS = "Press Enter or Space to grab.";

@Directive({
    selector: "[s25-ng-dnd-sortable-item]",
    standalone: false,
})
export class S25DndSortableItemDirective implements AfterViewInit, OnDestroy {
    @Input() index: number; // Has to be unique in sortable
    @Input() belongsTo?: any[]; // Only needed for nested sortables

    /** Set by the parent sortable while this item is grabbed via keyboard. */
    grabbed = false;

    listeners: ReturnType<Renderer2["listen"]>[] = [];

    constructor(
        public elementRef: ElementRef,
        private zone: NgZone,
        private renderer: Renderer2,
    ) {}

    ngAfterViewInit() {
        this.setEventListeners();
    }

    setEventListeners() {
        const elem = this.elementRef.nativeElement;
        this.zone.runOutsideAngular(() => {
            const events = [
                "dragstart",
                "dragend",
                "dragenter",
                "dragover",
                "focus",
                "keydown.enter",
                "keydown.space",
                "keydown.arrowup",
                "keydown.arrowdown",
                "keydown.arrowleft",
                "keydown.arrowright",
                "keydown.tab",
                "keydown.shift.tab",
                "keydown.escape",
            ];

            for (const event of events) {
                this.listeners.push(this.renderer.listen(elem, event, this.onEvent));
            }
        });
    }

    ngOnDestroy() {
        this.removeEventListeners();
    }

    removeEventListeners() {
        for (const listener of this.listeners) listener();
    }

    @HostBinding("class.dragging") dragging = false; // Add class when dragging with mouse
    @Input() @HostBinding("draggable") draggable = true; // Make item draggable

    /** Only when draggable so non-sortable hosts (e.g. table rows) keep their own tabindex / no false drag instructions. */
    @HostBinding("attr.tabindex")
    get tabIndex(): number | null {
        return this.draggable ? 0 : null;
    }

    @HostBinding("attr.aria-roledescription")
    get ariaRoleDescription(): string | null {
        return this.draggable ? "sortable item" : null;
    }

    // Add data to event
    // Wanted to use @HostBinding for this, but the test runner was complaining about custom attributes not existing
    @Bind
    onEvent(event: any) {
        if (event.type === "keydown" && event.target !== this.elementRef.nativeElement) return; // Only act on keyboard events originating on item
        if (event.type === "focus") {
            // VoiceOver often skips aria-describedby on generic focusable items; announce instead
            if (this.draggable && !this.grabbed) AriaLive.announce(S25_DND_SORTABLE_IDLE_INSTRUCTIONS);
            return;
        }
        if (event.type === "dragstart") this.dragging = true;
        if (event.type === "dragend") this.dragging = false;
        event["s25-dnd-sortable-item--index"] = this.index;
        event["s25-dnd-sortable-item--belongsTo"] = this.belongsTo;
        event["s25-dnd-sortable-item"] = true;
    }
}
